import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  FaUsers,
  FaChartLine,
  FaMoneyBillWave,
  FaStream,
  FaCircle,
  FaArrowRight,
  FaPlus
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Parse from 'parse';
import Agent from '../../models/Agent';
import UserModel from '../../models/User';
import HostStreamStats from '../../models/HostStreamStats';
import Host from '../../models/Host';
import { diamondsToUsd } from '../../utils/helpers';

// Format stream time from seconds to hours and minutes
const formatStreamTime = (seconds) => {
  if (!seconds) return '0h 0m';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}h ${minutes}m`;
};

const HostsManagement = () => {
  const { t } = useTranslation();
  const [hostsData, setHosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalHosts: 0,
    activeHosts: 0,
    hostsRevenue: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    const fetchHosts = async () => {
      setIsLoading(true);
      try {
        const currentUser = Parse.User.current();
        if (!currentUser) return;

        const agent = await Agent.getAgentByAuthorId(currentUser.id);
        if (!agent) return;

        // Get current month's dates for stats
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Get hosts data with earnings
        const { hosts: hostsWithEarnings, totalEarnings } = await Host.getHostsData(agent);

        // Get stream stats for additional metrics
        const streamStats = await HostStreamStats.getAgentDashboardStats(agent, startOfMonth, endOfMonth);

        // Get Hosts data
        const enrichedHostsData = hostsWithEarnings.map(host => {
          
          const user = host.author;
          let status = HostStreamStats.status.OFFLINE;
          const userStatus = user.get(UserModel.keys.STATUS);
          if (user.get(UserModel.keys.IS_LIVE_STREAMING)) {
            status = HostStreamStats.status.STREAMING;
          } else if (userStatus) {
            status = userStatus;
          }

          return {
            ...host,
            hostEarnings: host.hostEarnings,
            totalStreamTime: host.totalStreamTime,
            commissionSent: host.commissionSent,
            status: status
          };
        });

        setHosts(enrichedHostsData);

        // Calculate summary stats
        const totalHosts = agent.hostIds?.length || 0;
        const activeHosts = streamStats.activeHosts;
        const hostsRevenue = totalEarnings;
        const totalRevenue = agent.diamondsTotal;

        setStats({
          totalHosts,
          activeHosts,
          hostsRevenue,
          totalRevenue
        });

      } catch (error) {
        console.error('Error fetching hosts data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHosts();
  }, []);

  return (
    <Container>
      <Header>
        <Title>
          <FaUsers />
          {t('dashboard.hosts.title')}
        </Title>
        <AddHostButton onClick={() => {
          // TODO: Implement add host functionality
          toast.info('Add host functionality coming soon!');
        }}>
          <FaPlus />
          Add new host
        </AddHostButton>
      </Header>

      <StatsGrid>
        <StatCard>
          <div className="title">
            <FaUsers />
            {t('dashboard.overview.totalHosts')}
          </div>
          <div className="value">{stats.totalHosts}</div>
        </StatCard>

        <StatCard>
          <div className="title">
            <FaUsers />
            {t('dashboard.stats.activeHosts')}
          </div>
          <div className="value">{stats.activeHosts}</div>
          <div className="percentage">{stats.totalHosts > 0 ? Math.round((stats.activeHosts / stats.totalHosts) * 100) : 0}%</div>
        </StatCard>

        <StatCard>
          <div className="title">
            <FaMoneyBillWave />
            {t('dashboard.stats.hostEarnings')}
          </div>
          <div className="value">${diamondsToUsd(stats.hostsRevenue)}</div>
        </StatCard>

        <StatCard>
          <div className="title">
            <FaMoneyBillWave />
            {t('dashboard.overview.agencyEarnings')}
          </div>
          <div className="value">${diamondsToUsd(stats.totalRevenue)}</div>
        </StatCard>
      </StatsGrid>

      <ContentCard>
        <div className="card-header">
          <div className="card-title">
            <FaUsers />
            {t('dashboard.hosts.allHosts')}
          </div>
        </div>
        <Table>
          <thead>
            <tr>
              <th>{t('dashboard.stats.host')}</th>
              <th>{t('dashboard.stats.status')}</th>
              <th>{t('dashboard.stats.streams')}</th>
              <th>{t('dashboard.stats.duration')}</th>
              <th>{t('dashboard.stats.earnings')}</th>
              <th>{t('dashboard.stats.commissions')}</th>
              <th>{t('dashboard.stats.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                  {t('dashboard.hosts.loading')}
                </td>
              </tr>
            ) : hostsData.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                  {t('dashboard.hosts.noActiveHosts')}
                </td>
              </tr>
            ) : (
              hostsData.map(host => {
                // Get avatar initials
                const initials = host.name
                  ? host.name
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase()
                  : 'UN';

                return (
                  <tr key={host.hostId}>
                    <td>
                      <div className="host-cell">
                        {host.avatar ? (
                          <img
                            src={host.avatar}
                            alt={host.name}
                            className="avatar-img"
                          />
                        ) : (
                          <div className="avatar">{initials}</div>
                        )}
                        <div className="host-info">
                          <div className="name">{host.name || t('dashboard.hosts.unknownHost')}</div>
                          <div className="id">ID: {host.uid}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <StatusBadge className={host.status}>
                        <FaCircle /> {t(`dashboard.hosts.statusTypes.${host.status}`)}
                      </StatusBadge>
                    </td>
                    <td>
                      <div className="metrics-cell">
                        <FaStream /> {host.streamCount}
                      </div>
                    </td>
                    <td>
                      <div className="metrics-cell">
                        {formatStreamTime(host.totalStreamTime)}
                      </div>
                    </td>
                    <td>
                      <div className="metrics-cell">
                        <FaMoneyBillWave /> ${diamondsToUsd(host.hostEarnings)}
                      </div>
                    </td>
                    <td>
                      <div className="metrics-cell">
                        <FaMoneyBillWave /> ${diamondsToUsd(host.commissionSent)}
                      </div>
                    </td>
                    <td>
                      <ActionButton>
                        {t('dashboard.stats.viewDetails')}
                        <FaArrowRight />
                      </ActionButton>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </ContentCard>
    </Container>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Container = styled.div`
  padding: 2rem;
`;



const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a2a6c;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #1a2a6c;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const StatCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #1a2a6c, #b21f1f);
  }

  .title {
    color: #64748b;
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
      color: #1a2a6c;
    }
  }

  .value {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1a2a6c;
    margin-bottom: 0.5rem;
    letter-spacing: -0.5px;
  }

  .percentage {
    font-size: 1rem;
    color: #64748b;
    font-weight: 500;
  }
`;

const ContentCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .card-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #1a2a6c;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
      color: #1a2a6c;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th, td {
    padding: 1.25rem 1rem;
    text-align: left;
  }

  th {
    font-weight: 600;
    color: #64748b;
    font-size: 0.95rem;
    border-bottom: 2px solid #f1f5f9;
  }

  td {
    font-size: 0.95rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }

  tbody tr {
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8fafc;
    }
  }

  .host-cell {
    display: flex;
    align-items: center;
    gap: 1rem;

    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #e2e8f0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      color: #64748b;
    }
    
    .avatar-img {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid #e2e8f0;
    }

    .host-info {
      .name {
        font-weight: 600;
        color: #1a2a6c;
        margin-bottom: 0.25rem;
      }
      .id {
        font-size: 0.85rem;
        color: #64748b;
      }
    }
  }

  .metrics-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;

    svg {
      font-size: 0.9rem;
    }
  }
`;

const StatusBadge = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    font-size: 0.6rem;
    animation: ${pulse} 2s infinite;
  }

  &.streaming {
    background: #fef2f2;
    color: #ef4444;
  }

  &.online {
    background: #f0fdf4;
    color: #10b981;
  }

  &.offline {
    background: #f8fafc;
    color: #64748b;
  }
`;



const ActionButton = styled.button`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  background: #1a2a6c;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    font-size: 0.9rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: #2a3a7c;
    transform: translateX(2px);

    svg {
      transform: translateX(2px);
    }
  }
`;

const AddHostButton = styled.button`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(90deg, #1a2a6c, #b21f1f);
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  svg {
    font-size: 0.9rem;
  }

  &:hover {
    background: linear-gradient(90deg, #1a2a6c, #e74c3c);
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
  }
`;

export default HostsManagement;
